import { Link } from 'react-router-dom';

import { DataTable, type Column } from '../components/DataTable.tsx';
import { Pagination } from '../components/Pagination.tsx';
import { dateOnly, money, statusClass, statusLabel } from '../lib/format.ts';
import { usePaginatedList } from '../lib/usePaginatedList.ts';

interface Row {
  id: number;
  soNo: string;
  soDate: string;
  customerNo: string | null;
  customerName: string | null;
  salesman: string | null;
  currency: string;
  value: number;
  status: string;
  ref: string | null;
}

export function SalesOrdersListPage() {
  const list = usePaginatedList<Row>({
    endpoint: '/api/dashboard/sales-orders',
    defaultSortColumn: 'so_date',
    defaultSortDirection: 'DESC',
    filterKeys: ['status', 'customerNo'],
  });

  const columns: Column<Row>[] = [
    { key: 'so_no', label: 'SO no', sortable: true, className: 'mono', render: (r) => <Link to={`/sales-orders/${encodeURIComponent(r.soNo)}`}>{r.soNo}</Link> },
    { key: 'so_date', label: 'Date', sortable: true, className: 'mono', render: (r) => dateOnly(r.soDate) },
    { key: 'customer_no', label: 'Customer', sortable: true, render: (r) => r.customerNo ? <Link to={`/customers/${encodeURIComponent(r.customerNo)}`}>{r.customerName ?? r.customerNo}</Link> : '—' },
    { key: 'salesman', label: 'Salesman', render: (r) => r.salesman ?? '—' },
    { key: 'value', label: 'Value', sortable: true, className: 'num', render: (r) => money(r.value, r.currency) },
    { key: 'status', label: 'Status', sortable: true, render: (r) => <span className={`badge ${statusClass(r.status)}`}>{statusLabel(r.status)}</span> },
  ];

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Sales orders</h1>
        <span className="main-subtitle">{list.data ? `${list.data.total} total` : ''}</span>
      </header>
      <div className="toolbar">
        <input placeholder="Search SO no or ref…" defaultValue={list.state.search} onChange={(e) => list.setSearch(e.target.value)} />
        <select value={list.state.filters.status ?? ''} onChange={(e) => list.setFilter('status', e.target.value)}>
          <option value="">All statuses</option>
          <option value="O">Open</option>
          <option value="C">Closed</option>
          <option value="X">Cancelled</option>
        </select>
      </div>
      {list.error && <div className="error">{(list.error as Error).message}</div>}
      {list.isLoading && !list.data && <div className="loading">Loading…</div>}
      {list.data && (
        <>
          <DataTable columns={columns} rows={list.data.rows} rowKey={(r) => r.id} sortColumn={list.state.sortColumn} sortDirection={list.state.sortDirection} onSortChange={list.toggleSort} />
          <Pagination page={list.state.page} pageSize={list.state.pageSize} total={list.data.total} onPageChange={list.setPage} />
        </>
      )}
    </>
  );
}
